import {FC, useEffect, useState} from "react";
import {
   Alert,
   Box,
   Button,
   CircularProgress,
   Dialog,
   DialogActions,
   DialogContent,
   DialogTitle,
   Tab,
   Tabs,
   Typography
} from "@mui/material";
import {ConfigCompareQuery, DifferenceTypeEnum, useConfigCompareLazyQuery} from "../../../Queries";

type Shop = {
   _id: string;
   name: string | null;
};

type ComparisonResult = {
   comparisonName: string;
   nameA: string;
   nameB: string;
   totalDifferences: number;
   differencesByModel: Record<string, Array<{
      path: string;
      valueA: any;
      valueB: any;
      differenceType: DifferenceTypeEnum | null;
   }>>;
   error?: string;
};

type ConfigCompareDialogProps = {
   open: boolean;
   onClose: () => void;
   brandHouseId: string;
   shops: Shop[];
};

const formatValue = (value: any): string => {
   if (value === null || value === undefined) return '(empty)';
   if (typeof value === 'object') return JSON.stringify(value, null, 2);
   return String(value);
};

const formatDifferenceType = (type: DifferenceTypeEnum | null): string => {
   switch (type) {
      case DifferenceTypeEnum.VALUE_MISMATCH:
         return 'Different';
      case DifferenceTypeEnum.ONLY_IN_A:
         return 'Only in A';
      case DifferenceTypeEnum.ONLY_IN_B:
         return 'Only in B';
      case DifferenceTypeEnum.MISSING_IN_A:
         return 'Missing in A';
      case DifferenceTypeEnum.MISSING_IN_B:
         return 'Missing in B';
      default:
         return type || 'Unknown';
   }
};

const mapQueryResultToComparison = (
   data: ConfigCompareQuery,
   comparisonName: string
): ComparisonResult | null => {
   if (!data.ConfigCompare) return null;

   const result = data.ConfigCompare;
   const differencesByModel: Record<string, Array<{
      path: string;
      valueA: any;
      valueB: any;
      differenceType: DifferenceTypeEnum | null;
   }>> = {};

   (result.differences || []).forEach(diff => {
      if (!diff) return;
      const modelName = diff.modelName;
      if (!differencesByModel[modelName]) {
         differencesByModel[modelName] = [];
      }
      differencesByModel[modelName].push({
         path: diff.path,
         valueA: diff.valueA,
         valueB: diff.valueB,
         differenceType: diff.differenceType || null
      });
   });

   return {
      comparisonName,
      nameA: result.nameA,
      nameB: result.nameB,
      totalDifferences: result.summary?.totalDifferences || 0,
      differencesByModel
   };
};

export const ConfigCompareDialog: FC<ConfigCompareDialogProps> = ({
   open,
   onClose,
   brandHouseId,
   shops
}) => {
   const [loading, setLoading] = useState(false);
   const [results, setResults] = useState<ComparisonResult[]>([]);
   const [error, setError] = useState<string | null>(null);
   const [selectedTab, setSelectedTab] = useState(0);

   const [executeCompare] = useConfigCompareLazyQuery({
      fetchPolicy: 'no-cache'
   });

   useEffect(() => {
      if (open && shops.length > 0) {
         setSelectedTab(0);
         runComparisons();
      } else if (open && shops.length === 0) {
         setResults([]);
         setError(null);
         setSelectedTab(0);
      }
   }, [open]);

   const runComparisons = async () => {
      setLoading(true);
      setError(null);
      const allResults: ComparisonResult[] = [];

      try {
         // 1. BrandSetting comparisons: brandHouse vs each shop
         for (const shop of shops) {
            try {
               const {data} = await executeCompare({
                  variables: {
                     brandHouseIdA: brandHouseId,
                     shopIdB: shop._id,
                     models: ['BrandSetting']
                  }
               });
               if (data) {
                  const result = mapQueryResultToComparison(
                     data,
                     `BrandSetting: BrandHouse vs ${shop.name || 'Unknown'}`
                  );
                  if (result && result.totalDifferences > 0) {
                     allResults.push(result);
                  }
               }
            } catch (e) {
               allResults.push({
                  comparisonName: `BrandSetting: BrandHouse vs ${shop.name || 'Unknown'}`,
                  nameA: 'BrandHouse',
                  nameB: shop.name || 'Unknown',
                  totalDifferences: 0,
                  differencesByModel: {},
                  error: 'Failed to compare'
               });
            }
         }

         // 2. Other models: first shop vs all other shops
         if (shops.length > 1) {
            const firstShop = shops[0];
            const otherShops = shops.slice(1);

            for (const shop of otherShops) {
               try {
                  const {data} = await executeCompare({
                     variables: {
                        shopIdA: firstShop._id,
                        shopIdB: shop._id,
                        models: ['Shop', 'BrandRule', 'BrandLocalization', 'BrickMail']
                     }
                  });
                  if (data) {
                     const result = mapQueryResultToComparison(
                        data,
                        `Shop Config: ${firstShop.name || 'Unknown'} vs ${shop.name || 'Unknown'}`
                     );
                     if (result && result.totalDifferences > 0) {
                        allResults.push(result);
                     }
                  }
               } catch (e) {
                  allResults.push({
                     comparisonName: `Shop Config: ${firstShop.name || 'Unknown'} vs ${shop.name || 'Unknown'}`,
                     nameA: firstShop.name || 'Unknown',
                     nameB: shop.name || 'Unknown',
                     totalDifferences: 0,
                     differencesByModel: {},
                     error: 'Failed to compare'
                  });
               }
            }
         }

         setResults(allResults);
      } catch (e) {
         setError('Failed to execute comparisons');
      } finally {
         setLoading(false);
      }
   };

   const totalDifferences = results.reduce((sum, r) => sum + (r.error ? 0 : r.totalDifferences), 0);
   const hasErrors = results.some(r => r.error);
   const selectedResult = results[selectedTab];

   return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
         <DialogTitle>Configuration Comparison</DialogTitle>
         <DialogContent dividers sx={{p: 0}}>
            {loading ? (
               <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4}}>
                  <CircularProgress size={24}/>
                  <Typography sx={{ml: 2}}>Comparing configurations...</Typography>
               </Box>
            ) : error ? (
               <Box sx={{p: 2}}>
                  <Alert severity="error">{error}</Alert>
               </Box>
            ) : shops.length === 0 ? (
               <Box sx={{p: 2}}>
                  <Typography color="textSecondary">No shops to compare.</Typography>
               </Box>
            ) : results.length === 0 ? (
               <Box sx={{p: 2}}>
                  <Alert severity="success">All configurations match. No differences found.</Alert>
               </Box>
            ) : (
               <>
                  <Box sx={{borderBottom: 1, borderColor: 'divider'}}>
                     <Tabs
                        value={selectedTab}
                        onChange={(_, newValue) => setSelectedTab(newValue)}
                        variant="scrollable"
                        scrollButtons="auto"
                     >
                        {results.map((result, index) => (
                           <Tab
                              key={index}
                              label={
                                 <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                    <span>{result.comparisonName}</span>
                                    {result.error ? (
                                       <Box component="span" sx={{
                                          bgcolor: 'error.main',
                                          color: 'white',
                                          borderRadius: '10px',
                                          px: 1,
                                          fontSize: '0.75rem'
                                       }}>!</Box>
                                    ) : (
                                       <Box component="span" sx={{
                                          bgcolor: result.totalDifferences > 0 ? 'warning.main' : 'success.main',
                                          color: 'white',
                                          borderRadius: '10px',
                                          px: 1,
                                          fontSize: '0.75rem'
                                       }}>{result.totalDifferences}</Box>
                                    )}
                                 </Box>
                              }
                           />
                        ))}
                     </Tabs>
                  </Box>
                  <Box sx={{p: 2}}>
                     <Typography variant="body2" color="textSecondary" sx={{mb: 2}}>
                        {totalDifferences} difference{totalDifferences !== 1 ? 's' : ''} found across {results.length} comparison{results.length !== 1 ? 's' : ''}.
                        {hasErrors && ' Some comparisons failed.'}
                     </Typography>
                     {selectedResult && (
                        <Box sx={{maxHeight: '400px', overflowY: 'auto'}}>
                           {selectedResult.error ? (
                              <Alert severity="error">{selectedResult.error}</Alert>
                           ) : (
                              <>
                                 <Typography variant="body2" color="textSecondary" sx={{mb: 1}}>
                                    Comparing: {selectedResult.nameA} ↔ {selectedResult.nameB}
                                 </Typography>
                                 {Object.entries(selectedResult.differencesByModel).map(([modelName, diffs]) => (
                                    <Box key={modelName} sx={{mb: 2}}>
                                       <Typography variant="subtitle2" sx={{fontWeight: 500, mb: 1}}>
                                          {modelName} ({diffs.length})
                                       </Typography>
                                       {diffs.map((diff, idx) => (
                                          <Box key={idx} sx={{ml: 2, mb: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1}}>
                                             <Typography variant="body2" sx={{fontWeight: 500}}>
                                                {diff.path}
                                                <Typography component="span" variant="body2" sx={{ml: 1, color: '#666', fontWeight: 400}}>
                                                   ({formatDifferenceType(diff.differenceType)})
                                                </Typography>
                                             </Typography>
                                             <Box sx={{display: 'flex', gap: 1, mt: 0.5}}>
                                                <Typography
                                                   variant="body2"
                                                   sx={{
                                                      color: '#d32f2f',
                                                      flex: 1,
                                                      wordBreak: 'break-word',
                                                      whiteSpace: 'pre-wrap',
                                                      fontFamily: 'monospace',
                                                      fontSize: '0.8rem'
                                                   }}
                                                >
                                                   {formatValue(diff.valueA)}
                                                </Typography>
                                                <Typography variant="body2" sx={{color: '#666'}}>→</Typography>
                                                <Typography
                                                   variant="body2"
                                                   sx={{
                                                      color: '#2e7d32',
                                                      flex: 1,
                                                      wordBreak: 'break-word',
                                                      whiteSpace: 'pre-wrap',
                                                      fontFamily: 'monospace',
                                                      fontSize: '0.8rem'
                                                   }}
                                                >
                                                   {formatValue(diff.valueB)}
                                                </Typography>
                                             </Box>
                                          </Box>
                                       ))}
                                    </Box>
                                 ))}
                              </>
                           )}
                        </Box>
                     )}
                  </Box>
               </>
            )}
         </DialogContent>
         <DialogActions>
            <Button onClick={onClose}>Close</Button>
         </DialogActions>
      </Dialog>
   );
};
